/**
 * Product Page Functionality
 * Handles image gallery, variants, quantity, and add to cart
 */

class ProductPage {
  constructor() {
    this.productData = this.getProductData();
    this.currentVariant = null;
    
    this.mainImage = document.getElementById('mainProductImage');
    this.thumbnails = document.querySelectorAll('.thumbnail');
    this.variantSelects = document.querySelectorAll('.variant-select');
    this.quantityInput = document.getElementById('Quantity');
    this.qtyButtons = document.querySelectorAll('.qty-btn');
    this.addToCartBtn = document.getElementById('addToCartBtn');
    this.buyNowBtn = document.getElementById('buyNowBtn');
    this.productForm = document.getElementById('productForm');
    this.variantIdInput = document.getElementById('productVariantId');
    this.productPrice = document.getElementById('productPrice');
    this.productAvailability = document.getElementById('productAvailability');
    this.productSku = document.getElementById('productSku');
    this.productMessage = document.getElementById('productMessage');
    
    this.init();
  }

  init() {
    if (!this.productData) return;

    this.bindImageGallery();
    this.bindQuantityControls();
    this.bindVariantSelects();
    this.bindAddToCart();
    this.bindBuyNow();
    this.setInitialVariant();
  }

  /**
   * Get product JSON data
   */
  getProductData() {
    const productJSON = document.getElementById('productJSON');
    if (!productJSON) return null;
    
    try {
      return JSON.parse(productJSON.textContent);
    } catch (error) {
      console.error('Error parsing product JSON:', error);
      return null;
    }
  }

  /**
   * Bind image gallery thumbnails
   */
  bindImageGallery() {
    if (!this.thumbnails.length || !this.mainImage) return;

    this.thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', () => {
        this.handleThumbnailClick(thumbnail);
      });
    });
  }

  /**
   * Handle thumbnail click
   */
  handleThumbnailClick(thumbnail) {
    const imageId = parseInt(thumbnail.dataset.imageId);
    const newImage = this.productData.images[imageId];

    if (newImage && this.mainImage) {
      // Update main image
      this.mainImage.src = newImage.replace('.jpg', '_800x.jpg');
      this.mainImage.alt = this.productData.title;

      // Update active thumbnail
      this.thumbnails.forEach(t => t.classList.remove('active'));
      thumbnail.classList.add('active');
    }
  }

  /**
   * Bind quantity controls
   */
  bindQuantityControls() {
    if (!this.quantityInput) return;

    this.qtyButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.handleQuantityChange(button);
      });
    });

    // Prevent negative values
    this.quantityInput.addEventListener('keydown', (e) => {
      if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
        e.preventDefault();
      }
    });

    // Validate on blur
    this.quantityInput.addEventListener('blur', () => {
      this.validateQuantity();
    });
  }

  /**
   * Handle quantity button clicks
   */
  handleQuantityChange(button) {
    let value = parseInt(this.quantityInput.value) || 1;
    const minusButton = button.parentElement.querySelector('.minus');

    if (button.classList.contains('plus')) {
      value += 1;
      this.quantityInput.value = value;
      if (minusButton) minusButton.disabled = false;
    }

    if (button.classList.contains('minus') && value > 1) {
      value -= 1;
      this.quantityInput.value = value;
      
      if (value === 1 && minusButton) {
        minusButton.disabled = true;
      }
    }
  }

  /**
   * Validate quantity input
   */
  validateQuantity() {
    let value = parseInt(this.quantityInput.value) || 1;
    
    if (value < 1) {
      value = 1;
    }
    
    this.quantityInput.value = value;

    // Update minus button state
    const minusButton = document.querySelector('.qty-btn.minus');
    if (minusButton) {
      minusButton.disabled = value === 1;
    }
  }

  /**
   * Bind variant select dropdowns
   */
  bindVariantSelects() {
    if (!this.variantSelects.length) return;

    this.variantSelects.forEach(select => {
      select.addEventListener('change', () => {
        this.handleVariantChange();
      });
    });
  }

  /**
   * Set initial variant
   */
  setInitialVariant() {
    if (!this.productData.variants || this.productData.variants.length === 0) return;

    this.currentVariant = this.productData.variants[0];
    this.updateVariantDetails(this.currentVariant);
  }

  /**
   * Handle variant selection change
   */
  handleVariantChange() {
    const selectedOptions = Array.from(this.variantSelects).map(select => select.value);
    
    // Find matching variant
    const variant = this.productData.variants.find(v => {
      return v.options.every((option, index) => option === selectedOptions[index]);
    });

    if (variant) {
      this.currentVariant = variant;
      this.updateVariantDetails(variant);
    }
  }

  /**
   * Update product details based on selected variant
   */
  updateVariantDetails(variant) {
    // Update variant ID
    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }

    // Update price
    if (this.productPrice) {
      this.updatePrice(variant);
    }

    // Update availability
    if (this.productAvailability) {
      this.updateAvailability(variant);
    }

    // Update SKU
    if (this.productSku && variant.sku) {
      this.productSku.textContent = variant.sku;
    }

    // Update add to cart button
    if (this.addToCartBtn) {
      if (variant.available) {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.textContent = 'Add to Cart';
      } else {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.textContent = 'Sold Out';
      }
    }

    // Update buy now button
    if (this.buyNowBtn) {
      if (variant.available) {
        this.buyNowBtn.disabled = false;
        this.buyNowBtn.textContent = 'Buy Now';
      } else {
        this.buyNowBtn.disabled = true;
        this.buyNowBtn.textContent = 'Sold Out';
      }
    }

    // Update featured image if variant has one
    if (variant.featured_image && this.mainImage) {
      this.mainImage.src = variant.featured_image.src.replace('.jpg', '_800x.jpg');
    }
  }

  /**
   * Update price display
   */
  updatePrice(variant) {
    const price = this.formatMoney(variant.price);
    const compareAtPrice = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : null;

    if (compareAtPrice && variant.compare_at_price > variant.price) {
      const savings = this.formatMoney(variant.compare_at_price - variant.price);
      this.productPrice.innerHTML = `
        <span class="price-sale">${price}</span>
        <span class="price-compare">${compareAtPrice}</span>
        <span class="price-badge">Save ${savings}</span>
      `;
    } else {
      this.productPrice.innerHTML = `<span class="price-regular">${price}</span>`;
    }
  }

  /**
   * Update availability display
   */
  updateAvailability(variant) {
    if (variant.available) {
      this.productAvailability.innerHTML = `
        <span class="in-stock">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          In Stock
        </span>
      `;
    } else {
      this.productAvailability.innerHTML = `<span class="out-of-stock">Out of Stock</span>`;
    }
  }

  /**
   * Bind add to cart form submission
   */
  bindAddToCart() {
    if (!this.productForm) return;

    this.productForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleAddToCart();
    });
  }

  /**
   * Bind buy now button
   */
  bindBuyNow() {
    if (!this.buyNowBtn) return;

    this.buyNowBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleBuyNow();
    });
  }

  /**
   * Handle add to cart
   */
  async handleAddToCart() {
    const variantId = this.variantIdInput.value;
    const quantity = parseInt(this.quantityInput.value) || 1;

    // Set loading state
    this.addToCartBtn.classList.add('loading');
    this.addToCartBtn.disabled = true;

    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: quantity
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        this.showMessage('Product added to cart!', 'success');
        
        // Update cart count if cart icon exists
        this.updateCartCount();
        
        // Optional: Redirect to cart after delay
        setTimeout(() => {
          // window.location.href = '/cart';
        }, 1500);
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showMessage('Error adding product to cart. Please try again.', 'error');
    } finally {
      // Remove loading state
      this.addToCartBtn.classList.remove('loading');
      this.addToCartBtn.disabled = false;
    }
  }

  /**
   * Handle buy now
   */
  async handleBuyNow() {
    const variantId = this.variantIdInput.value;
    const quantity = parseInt(this.quantityInput.value) || 1;

    // Set loading state
    this.buyNowBtn.classList.add('loading');
    this.buyNowBtn.disabled = true;

    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: quantity
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        // Redirect to checkout immediately
        window.location.href = '/checkout';
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error with buy now:', error);
      this.showMessage('Error processing your request. Please try again.', 'error');
      
      // Remove loading state
      this.buyNowBtn.classList.remove('loading');
      this.buyNowBtn.disabled = false;
    }
  }

  /**
   * Update cart count
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count if element exists
      const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Show success/error message
   */
  showMessage(message, type) {
    if (!this.productMessage) return;

    this.productMessage.textContent = message;
    this.productMessage.className = `product-message ${type}`;

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.productMessage.style.transition = 'opacity 0.3s';
      this.productMessage.style.opacity = '0';
      setTimeout(() => {
        this.productMessage.className = 'product-message';
        this.productMessage.style.opacity = '1';
      }, 300);
    }, 5000);
  }

  /**
   * Format money (Shopify format)
   */
  formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductPage();
  });
} else {
  new ProductPage();
}

// Export for use in other scripts if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductPage;
}
